import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import SearchIcon from '@mui/icons-material/Search'
import {
  deviceIdKey,
  normalizeHiddenDeviceIds,
  reconcileHiddenDeviceIds
} from './hiddenDeviceIds'

/**
 * The two-column panel (Visible / Hidden) that both MOS widgets in story 157 use to choose which
 * devices they hide. One single component on purpose: the partition (I3), the reconciliation
 * (RN26), the purge safeguard (RN27) and the type-tolerant identifier comparison (RN28) are the
 * same problem in both, and duplicating them is how one of the copies ends up without the
 * safeguard.
 *
 * It does not know where the offerable devices come from: each widget brings its own source, and
 * whatever that source needs before the panel sees it — deduplicating a group listing (RN35), for
 * instance — belongs to the caller.
 *
 * ⚠️ RN27 — it shows already reconciled but only writes when the user moves something (DP5), and
 * it never writes at all while the offerable set is not reliable. A load that failed, is still in
 * flight or came back empty leaves the stored list exactly as it was: that is the line between a
 * purge and a data loss.
 */
const DeviceVisibilityPanel = ({
  offerableDevices,
  loading,
  error,
  hiddenDeviceIds,
  onChange,
  t_,
  emptyOfferableMessage
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedIdentities, setSelectedIdentities] = useState([])

  const hiddenIdentities = useMemo(() => normalizeHiddenDeviceIds(hiddenDeviceIds), [hiddenDeviceIds])

  const isReliable = !loading && !error && offerableDevices.length > 0

  const { visibleDevices, hiddenDevices } = useMemo(() => {
    const hidden = new Set(hiddenIdentities)
    return {
      visibleDevices: offerableDevices.filter((device) => !hidden.has(deviceIdKey(device.id))),
      hiddenDevices: offerableDevices.filter((device) => hidden.has(deviceIdKey(device.id)))
    }
  }, [offerableDevices, hiddenIdentities])

  // The panel's own search is client side and filters the Visible column ONLY: the Hidden column
  // stays whole, so the user never loses sight of what is already hidden (DP4).
  const searchedVisibleDevices = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (term === '') {
      return visibleDevices
    }
    return visibleDevices.filter((device) => device.name?.toLowerCase().includes(term))
  }, [visibleDevices, searchTerm])

  const writeHidden = (nextIdentities) => {
    setSelectedIdentities([])
    onChange(reconcileHiddenDeviceIds(nextIdentities, offerableDevices))
  }

  const toggleSelection = (identity) => {
    setSelectedIdentities((current) => current.includes(identity)
      ? current.filter((selected) => selected !== identity)
      : [...current, identity])
  }

  const identitiesOf = (devices) => devices.map((device) => deviceIdKey(device.id))

  const hideSelected = () => {
    const selectedVisible = identitiesOf(visibleDevices).filter((identity) => selectedIdentities.includes(identity))
    writeHidden([...hiddenIdentities, ...selectedVisible])
  }

  const showSelected = () => {
    writeHidden(hiddenIdentities.filter((identity) => !selectedIdentities.includes(identity)))
  }

  const hideAll = () => writeHidden(identitiesOf(offerableDevices))

  const showAll = () => writeHidden([])

  const renderColumn = (label, devices) => (
    <Paper variant='outlined' sx={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column' }}>
      <Typography variant='subtitle2' sx={{ p: 1 }} color='text.secondary'>
        {t_(label)}
      </Typography>
      <Divider />
      <List dense aria-label={t_(label)} sx={{ maxHeight: 260, overflowY: 'auto', flexGrow: 1 }}>
        {devices.map((device) => {
          const identity = deviceIdKey(device.id)
          return (
            <ListItemButton
              key={identity}
              onClick={() => toggleSelection(identity)}
              selected={selectedIdentities.includes(identity)}
              dense
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox
                  edge='start'
                  size='small'
                  tabIndex={-1}
                  disableRipple
                  checked={selectedIdentities.includes(identity)}
                  inputProps={{ 'aria-label': device.name }}
                />
              </ListItemIcon>
              <ListItemText primary={device.name} primaryTypographyProps={{ variant: 'body2', noWrap: true }} />
            </ListItemButton>
          )
        })}
      </List>
    </Paper>
  )

  const renderState = (content) => (
    <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>{content}</Box>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant='h6' fontWeight='bold' color='primary'>
        {t_('Device visibility')}
      </Typography>

      {loading
        ? renderState(<CircularProgress size={24} />)
        : null}

      {!loading && error
        ? renderState(
          <Alert severity='error' variant='outlined' sx={{ width: '100%' }}>
            {t_('Error loading devices')}
          </Alert>
        )
        : null}

      {!loading && !error && offerableDevices.length === 0
        ? renderState(
          <Typography variant='body2' color='text.secondary'>
            {t_(emptyOfferableMessage)}
          </Typography>
        )
        : null}

      {isReliable
        ? (
          <TextField
            size='small'
            fullWidth
            placeholder={t_('Search devices...')}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' color='action' />
                </InputAdornment>
              )
            }}
          />
          )
        : null}

      {!loading && !error
        ? (
          <Stack direction='row' spacing={1} alignItems='stretch'>
            {renderColumn('Visible devices', searchedVisibleDevices)}
            {isReliable
              ? (
                <Stack justifyContent='center' spacing={0.5}>
                  <IconButton
                    size='small'
                    aria-label={t_('Hide all devices')}
                    onClick={hideAll}
                    disabled={visibleDevices.length === 0}
                  >
                    <KeyboardDoubleArrowRightIcon fontSize='small' />
                  </IconButton>
                  <IconButton
                    size='small'
                    aria-label={t_('Hide selected devices')}
                    onClick={hideSelected}
                    disabled={selectedIdentities.length === 0}
                  >
                    <ChevronRightIcon fontSize='small' />
                  </IconButton>
                  <IconButton
                    size='small'
                    aria-label={t_('Show selected devices')}
                    onClick={showSelected}
                    disabled={selectedIdentities.length === 0}
                  >
                    <ChevronLeftIcon fontSize='small' />
                  </IconButton>
                  <IconButton
                    size='small'
                    aria-label={t_('Show all devices')}
                    onClick={showAll}
                    disabled={hiddenDevices.length === 0}
                  >
                    <KeyboardDoubleArrowLeftIcon fontSize='small' />
                  </IconButton>
                </Stack>
                )
              : null}
            {renderColumn('Hidden devices', hiddenDevices)}
          </Stack>
          )
        : null}
    </Box>
  )
}

DeviceVisibilityPanel.propTypes = {
  /** The offerable set of the widget that hosts the panel, already deduplicated by the caller. */
  offerableDevices: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string
  })).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  /** The stored value, in whatever shape it was stored: the panel normalises it (RN24). */
  hiddenDeviceIds: PropTypes.any,
  /** Called ONLY when the user moves something, with the already reconciled list (RN26, DP5). */
  onChange: PropTypes.func.isRequired,
  t_: PropTypes.func.isRequired,
  /** Literal for an offerable set that came back empty — each widget reuses its own (RN12). */
  emptyOfferableMessage: PropTypes.string.isRequired
}

DeviceVisibilityPanel.defaultProps = {
  loading: false,
  error: false
}

export default DeviceVisibilityPanel
